import { Controller } from '@hotwired/stimulus'

// Kept in step with Admin::DateRange's constants.
const INVALID_RANGE_MESSAGE = "End date can't be earlier than the start date"
const UNREADABLE_MESSAGE = 'Enter a date as MM/DD/YYYY'

// Each easepick field keeps the ISO date in a hidden partner input — that is
// what the server sees, and it sorts lexicographically, so the two ends are
// compared on it rather than on the MM/DD/YYYY text the user reads.
const toDisplay = (iso) => {
  const [ year, month, day ] = iso.split('-')
  return `${month}/${day}/${year}`
}

// Links a "from"/"to" easepick pair: locks the end picker's minimum date to
// the start date, snaps the end date forward if it would end up before it,
// and blocks submission of an inverted range typed in by hand.
export default class extends Controller {
  static targets = ['startDate', 'endDate']

  initializeEasepick () {
    if (this.initialized) return
    this.initialized = true

    const startDate = this.#isoValue(this.startDateTarget)
    if (startDate) {
      this.#updateEndDateMin(startDate)
    }
  }

  setStartDate (e) {
    const { date } = e.detail
    if (!date) return

    const startDate = date.format('YYYY-MM-DD')

    this.#adjustEndDate(startDate)
    this.#updateEndDateMin(startDate)
    this.validate()
  }

  // The pickers can't produce a bad value, but the fields stay typeable, so the
  // pair is re-checked on every edit. Both problems are reported through the
  // browser's own validation bubble, which also stops the form submitting.
  validate () {
    const start = this.#checkField(this.startDateTarget)
    const end = this.#checkField(this.endDateTarget)

    if (start && end && start > end) {
      this.endDateTarget.setCustomValidity(INVALID_RANGE_MESSAGE)
    }
  }

  // A date the mask never let the user finish leaves the hidden field empty:
  // there is text in the box but no date behind it, which is what gets flagged.
  #checkField (field) {
    const iso = this.#isoValue(field)

    field.setCustomValidity(field.value.trim() && !iso ? UNREADABLE_MESSAGE : '')

    return iso
  }

  #isoValue (field) {
    return document.getElementById(field.dataset.easepickValueId)?.value || null
  }

  #updateEndDateMin (startDate) {
    this.endDateTarget.setAttribute('data-easepick-min-date', toDisplay(startDate))
    const controller = this.#easepickController(this.endDateTarget)
    if (controller?.picker) {
      controller.reload()
    }
  }

  #adjustEndDate (startDate) {
    const endDate = this.#isoValue(this.endDateTarget)
    if (!endDate) return

    if (startDate > endDate) {
      this.endDateTarget.value = toDisplay(startDate)
      this.endDateTarget.dispatchEvent(new Event('change'))
    }
  }

  #easepickController (element) {
    return this.application.getControllerForElementAndIdentifier(element, 'easepick')
  }
}
